import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { useQuery } from '@apollo/client';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import {
  ExternalIntegrationsNamespaceInv360VehiclesArgs,
  Inv360VehicleObject,
  Query,
} from '@lgg/isomorphic/types/__generated__/graphql';
import {
  DropdownButtonIcon,
  LggSelectableOptionsDropdownButton,
} from 'src/components/general/button/dropdown-button';
import { ButtonV2 } from 'src/components/general/button/lgg-button';
import { SelectableOptionsDrawer } from 'src/components/general/drawer/bottom/selectable-options-bottom-drawer';
import { UnDecoratedTextInput } from 'src/components/general/inputs/text-input';
import { FlexRow } from 'src/components/layout/flex-row';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useDebouncedState } from 'src/hooks/use-debounced-state';
import { useFetchMoreWithErrorHandling } from 'src/hooks/use-fetch-more-with-error-handling';
import { useVisible } from 'src/hooks/use-visible';
import {
  INV360_VEHICLES_QUERY,
  Inv360SortOption,
  PER_PAGE,
  SORT_OPTIONS,
} from '../operations';
import {
  DrawerBody,
  StatusMessage,
  StickyFooter,
  Thumb,
  ThumbInfoWrapper,
  ToggleCircle,
  VehicleInfo,
  VehicleMeta,
  VehiclePrice,
  VehicleTitle,
} from './shared';

const SearchBar = styled.div`
  align-items: flex-start;
  background: ${({ theme }) => theme.colors.alabaster};
  border-bottom: 1px solid ${({ theme }) => theme.colors.koala};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 64px;
  padding: 15px 20px;
`;

const SearchInput = styled(UnDecoratedTextInput)`
  align-self: stretch;
  width: 100%;

  [data-lgg-id='text-input-clear-icon'] {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
  }

  input {
    height: 34px;
    padding: 9px 32px 9px 10px;
  }

  input::placeholder {
    color: ${({ theme }) => theme.colors.geyser};
    font-family: ${({ theme }) => theme.font.regular};
    font-size: 14px;
    line-height: 16px;
  }

  svg path {
    fill: ${({ theme }) => theme.colors.casper};
  }
`;

const SortRow = styled(FlexRow)`
  align-items: center;
  background: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.koala};
  box-sizing: border-box;
  height: 64px;
  justify-content: space-between;
  padding: 15px 20px;
`;

const ResultCount = styled.span`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 15px;
  line-height: 19px;
`;

const sortTriggerStyles = `
  align-items: center;
  border-radius: 4px;
  display: flex;
  gap: 10px;
  line-height: 18px;
  padding: 8px 10px;
`;

const SortDropdownButton = styled(LggSelectableOptionsDropdownButton)`
  ${sortTriggerStyles}
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.koala};
`;

const SortMobileButton = styled(ButtonV2)`
  ${sortTriggerStyles}

  & > span {
    align-items: center;
    display: flex;
  }

  ${DropdownButtonIcon} {
    height: 12px;
    width: 12px;

    svg {
      height: 12px;
      width: 12px;
    }
  }
`;

const ListContainer = styled.div`
  background: ${({ theme }) => theme.colors.white};
  flex: 1;
  min-height: 0;
`;

const VehicleRow = styled(FlexRow)`
  align-items: flex-start;
  background: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.porcelain};
  box-sizing: border-box;
  cursor: pointer;
  gap: 10px;
  height: 110px;
  padding: 20px;
  position: relative;
`;

const SelectedHighlight = styled.div`
  background: ${({ theme }) => theme.colors.secondaryTopaz10};
  border: 1px solid ${({ theme }) => theme.colors.secondaryTopaz60};
  border-radius: 4px;
  inset: 5px 5px 4px 5px;
  position: absolute;
`;

const VehicleRowContent = styled(FlexRow)`
  align-items: center;
  flex: 1 0 0;
  gap: 20px;
  min-width: 0;
  position: relative;
`;

const NextButton = styled(ButtonV2)`
  width: 100%;
`;

type Inv360VehiclePickerProps = {
  institutionId: number;
  search: string;
  setSearch: (value: string) => void;
  sort: Inv360SortOption;
  setSort: (value: Inv360SortOption) => void;
  selectedIds: number[];
  toggleSelected: (vehicle: Inv360VehicleObject) => void;
  selectedVehiclesById: Map<number, Inv360VehicleObject>;
  onNext: VoidFunction;
};

export const Inv360VehiclePicker = memo<Inv360VehiclePickerProps>(
  ({
    institutionId,
    search,
    setSearch,
    sort,
    setSort,
    selectedIds,
    toggleSelected,
    selectedVehiclesById,
    onNext,
  }) => {
    const { t } = useTranslation(['conversations']);
    const debouncedSearch = useDebouncedState(search, 350);
    const breakpointUpMd = useBreakpoint(up('md'));
    const sortVisibilityHandler = useVisible();
    const [loadingMore, setLoadingMore] = useState(false);

    const queryInput = useMemo(
      () => ({
        institutionId,
        perPage: PER_PAGE,
        sort,
        search: debouncedSearch || undefined,
      }),
      [institutionId, sort, debouncedSearch],
    );

    const {
      data,
      previousData,
      loading: isLoading,
      error: queryError,
      fetchMore,
    } = useQuery<
      Pick<Query, 'externalIntegrations'>,
      ExternalIntegrationsNamespaceInv360VehiclesArgs
    >(INV360_VEHICLES_QUERY, {
      variables: { input: { ...queryInput, page: 1 } },
      fetchPolicy: 'network-only',
    });
    const fetchMoreWithErrorHandling = useFetchMoreWithErrorHandling<
      Pick<Query, 'externalIntegrations'>,
      ExternalIntegrationsNamespaceInv360VehiclesArgs
    >(fetchMore);

    const virtuosoRef = useRef<VirtuosoHandle>(null);
    const vehiclesPage = data?.externalIntegrations.inv360Vehicles;
    const vehicleList = useMemo(() => vehiclesPage?.data ?? [], [vehiclesPage]);

    useEffect(() => {
      virtuosoRef.current?.scrollToIndex({ index: 0 });
    }, [debouncedSearch, sort]);

    const sortOptions = useMemo(
      () =>
        SORT_OPTIONS.map((option) => ({
          value: option,
          label: t(`conversations:messageInput.options.inv360.sortOptions.${option}`),
          onClick: () => {
            setSort(option);
            sortVisibilityHandler.close();
          },
          'data-lgg-id': `inv360-sort-option-${option}`,
        })),
      [setSort, sortVisibilityHandler, t],
    );

    const loadMore = useCallback(() => {
      const currentPage = vehiclesPage?.page ?? 1;
      const totalPages = vehiclesPage?.totalPages ?? 0;

      if (isLoading || loadingMore || totalPages <= currentPage) {
        return;
      }

      setLoadingMore(true);
      void fetchMoreWithErrorHandling({
        variables: { input: { ...queryInput, page: currentPage + 1 } },
      }).finally(() => setLoadingMore(false));
    }, [vehiclesPage, isLoading, loadingMore, fetchMoreWithErrorHandling, queryInput]);

    const selectedCount = selectedIds.length;
    const hasResults = vehicleList.length > 0;

    const vehicleListContent = useMemo(() => {
      const statusMessage = queryError
        ? t('conversations:messageInput.options.inv360.errors.loadFailed')
        : !hasResults && isLoading
        ? t('conversations:messageInput.options.inv360.loading')
        : !hasResults
        ? t('conversations:messageInput.options.inv360.noResults')
        : null;

      if (statusMessage) {
        return <StatusMessage>{statusMessage}</StatusMessage>;
      }

      return (
        <Virtuoso
          ref={virtuosoRef}
          style={{ height: '100%' }}
          data={vehicleList}
          endReached={loadMore}
          computeItemKey={(_, vehicle) => vehicle.id}
          components={{
            Footer: () =>
              loadingMore ? (
                <StatusMessage>
                  {t('conversations:messageInput.options.inv360.loadingMore')}
                </StatusMessage>
              ) : null,
          }}
          itemContent={(_, vehicle) => {
            const isSelected = selectedVehiclesById.has(vehicle.id);
            const mileageDays = [
              vehicle.mileage
                ? t('conversations:messageInput.options.inv360.mileage', {
                    count: vehicle.mileage,
                  })
                : null,
              vehicle.daysInInventory != null
                ? t('conversations:messageInput.options.inv360.daysInInventory', {
                    count: vehicle.daysInInventory,
                  })
                : null,
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <VehicleRow
                onClick={() => toggleSelected(vehicle)}
                data-lgg-id="inv360-vehicle-row"
              >
                {isSelected && <SelectedHighlight />}
                <VehicleRowContent>
                  <ThumbInfoWrapper>
                    <Thumb src={vehicle.thumbnailUrl ?? undefined} alt={vehicle.title} />
                    <VehicleInfo>
                      <VehicleTitle>{vehicle.title}</VehicleTitle>
                      <VehiclePrice>{vehicle.formattedPrice}</VehiclePrice>
                      {vehicle.vin ? <VehicleMeta>{vehicle.vin}</VehicleMeta> : null}
                      {mileageDays ? <VehicleMeta>{mileageDays}</VehicleMeta> : null}
                    </VehicleInfo>
                  </ThumbInfoWrapper>
                  <ToggleCircle
                    $selected={isSelected}
                    icon={isSelected ? 'remove' : 'add'}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSelected(vehicle);
                    }}
                    aria-label={isSelected ? 'Remove' : 'Add'}
                    data-lgg-id="inv360-vehicle-toggle"
                  />
                </VehicleRowContent>
              </VehicleRow>
            );
          }}
        />
      );
    }, [
      queryError,
      hasResults,
      isLoading,
      vehicleList,
      loadMore,
      loadingMore,
      selectedVehiclesById,
      toggleSelected,
      t,
    ]);

    return (
      <DrawerBody>
        <SearchBar>
          <SearchInput
            placeholder={t('conversations:messageInput.options.inv360.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch('')}
            data-lgg-id="inv360-search-input"
          />
        </SearchBar>
        <SortRow>
          <ResultCount data-lgg-id="inv360-result-count">
            {t('conversations:messageInput.options.inv360.resultCount', {
              count:
                data?.externalIntegrations.inv360Vehicles.total ??
                previousData?.externalIntegrations.inv360Vehicles.total ??
                0,
            })}
          </ResultCount>
          {breakpointUpMd ? (
            <SortDropdownButton
              size="small"
              variant="defaultGhost"
              selectedValue={sort}
              options={sortOptions}
              visibilityHandler={sortVisibilityHandler}
              data-lgg-id="inv360-sort-trigger"
            >
              {t(`conversations:messageInput.options.inv360.sortOptions.${sort}`)}
            </SortDropdownButton>
          ) : (
            <>
              <SortMobileButton
                size="small"
                variant="defaultGhost"
                onClick={sortVisibilityHandler.show}
                data-lgg-id="inv360-sort-trigger"
              >
                {t(`conversations:messageInput.options.inv360.sortOptions.${sort}`)}
                <DropdownButtonIcon
                  type="arrowdown"
                  className={sortVisibilityHandler.visible ? 'active' : undefined}
                />
              </SortMobileButton>
              <SelectableOptionsDrawer
                visible={sortVisibilityHandler.visible}
                onClose={sortVisibilityHandler.close}
                title={t('conversations:messageInput.options.inv360.sortBy')}
                options={sortOptions}
                selectedValue={sort}
              />
            </>
          )}
        </SortRow>
        <ListContainer data-lgg-id="inv360-vehicle-list">
          {vehicleListContent}
        </ListContainer>
        <StickyFooter>
          <NextButton
            size="regular"
            variant="primary"
            disabled={selectedCount === 0}
            onClick={onNext}
            data-lgg-id="inv360-next-button"
          >
            {selectedCount === 0
              ? t('conversations:messageInput.options.inv360.next')
              : t('conversations:messageInput.options.inv360.nextWithCount', {
                  count: selectedCount,
                })}
          </NextButton>
        </StickyFooter>
      </DrawerBody>
    );
  },
);
